import React, { useState, useEffect, useRef } from 'react';
import { FaFileAlt, FaFilePdf, FaFileImage, FaDownload, FaTrash, FaUpload, FaSpinner } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { useToast } from '../utils/toastContext';

const DOC_TYPES = ['ID', 'Passport', 'Bank Letter'];

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf';

const docTypeColor = {
    'ID': { bg: 'linear-gradient(135deg,#dbeafe 0%,#bfdbfe 100%)', color: '#1d4ed8', border: '#93c5fd' },
    'Passport': { bg: 'linear-gradient(135deg,#d1fae5 0%,#a7f3d0 100%)', color: '#065f46', border: '#6ee7b7' },
    'Bank Letter': { bg: 'linear-gradient(135deg,#ede9fe 0%,#ddd6fe 100%)', color: '#5b21b6', border: '#c4b5fd' },
};

function FileIcon({ contentType }) {
    if (!contentType) return <FaFileAlt />;
    if (contentType === 'application/pdf') return <FaFilePdf style={{ color: '#dc2626' }} />;
    return <FaFileImage style={{ color: '#0284c7' }} />;
}

export default function UserDocuments({ userId }) {
    const { showError, showSuccess } = useToast();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [docType, setDocType] = useState('');
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const fetchDocs = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/crud/get_user_documents/${userId}`);
            setDocs(res.data || []);
        } catch {
            showError('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) fetchDocs();
    }, [userId]);

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) setFile(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) setFile(f);
    };

    const handleUpload = async () => {
        if (!docType) { showError('Please select a document type'); return; }
        if (!file) { showError('Please select a file'); return; }

        const form = new FormData();
        form.append('user_id', userId);
        form.append('doc_type', docType);
        form.append('file', file);

        try {
            setUploading(true);
            await axiosInstance.post('/crud/upload_user_document/', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccess('Document uploaded successfully');
            setFile(null);
            setDocType('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchDocs();
        } catch (err) {
            showError(err?.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (docId) => {
        try {
            setDeletingId(docId);
            await axiosInstance.delete(`/crud/delete_user_document/${docId}`);
            showSuccess('Document deleted');
            setDocs(prev => prev.filter(d => d.doc_id !== docId));
        } catch {
            showError('Failed to delete document');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (docId, filename) => {
        try {
            const res = await axiosInstance.get(`/crud/download_user_document/${docId}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            showError('Failed to download document');
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '0.875rem 1rem',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '0.9rem',
        fontWeight: 500,
        outline: 'none',
        background: 'linear-gradient(135deg,#fafafa 0%,#f8fafc 100%)',
        boxSizing: 'border-box',
        cursor: 'pointer',
        transition: 'border-color 0.2s'
    };

    const actionButtonStyle = {
        minHeight: '44px',
        padding: '0.75rem 1rem',
        borderRadius: '10px',
        fontSize: '0.875rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 0.2s',
        boxSizing: 'border-box'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
                background: 'linear-gradient(140deg, rgba(10, 30, 52, 0.9) 0%, rgba(21, 45, 72, 0.82) 100%)',
                borderRadius: '18px',
                padding: '1rem 1.1rem',
                border: '1px solid rgba(193, 212, 233, 0.52)',
                boxShadow: '0 18px 38px rgba(22, 54, 86, 0.16)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)'
                }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1rem',
                        boxShadow: '0 2px 8px rgba(43, 70, 99, 0.2)',
                        flexShrink: 0
                    }}>
                        <FaFileAlt />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#eff6ff', letterSpacing: '-0.025em' }}>
                            Personal Documents
                        </h2>
                        <p style={{ margin: 0, color: '#cadcf0', fontSize: '0.84rem', fontWeight: 500 }}>
                            Upload and manage ID, passport, and bank documents
                        </p>
                    </div>
                </div>
            </div>

            {/* Upload card */}
            <div style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '14px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                marginBottom: '1.5rem'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
                    gap: '1rem',
                    alignItems: 'end'
                }}>
                    {/* Doc type */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.825rem', fontWeight: 700, color: '#374151', letterSpacing: '0.02em' }}>
                            Document Type
                        </label>
                        <select
                            value={docType}
                            onChange={e => setDocType(e.target.value)}
                            style={inputStyle}
                            onFocus={e => { e.target.style.borderColor = '#4a90e2'; }}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                        >
                            <option value="">Select document type...</option>
                            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {/* File drop zone */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.825rem', fontWeight: 700, color: '#374151', letterSpacing: '0.02em' }}>
                            File
                        </label>
                        <div
                            style={{
                                border: `2px dashed ${dragOver ? '#4a90e2' : file ? '#22c55e' : '#cbd5e1'}`,
                                borderRadius: '10px',
                                padding: '0.75rem 1rem',
                                background: dragOver ? '#eff6ff' : file ? '#f0fdf4' : '#f8fafc',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                minHeight: '46px'
                            }}
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                        >
                            <FaUpload style={{ color: file ? '#16a34a' : '#94a3b8', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.85rem', color: file ? '#15803d' : '#94a3b8', fontWeight: file ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file ? file.name : 'Click or drag a file here'}
                            </span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPT}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </div>
                        <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                            PNG, JPG, WEBP, GIF or PDF
                        </p>
                    </div>

                    {/* Upload button */}
                    <div>
                        <button
                            onClick={handleUpload}
                            disabled={uploading || !docType || !file}
                            style={{
                                width: '100%',
                                ...actionButtonStyle,
                                background: (uploading || !docType || !file)
                                    ? 'linear-gradient(135deg,#cbd5e1 0%,#94a3b8 100%)'
                                    : 'linear-gradient(135deg,#2b4663 0%,#4a90e2 100%)',
                                border: 'none',
                                color: 'white',
                                cursor: (uploading || !docType || !file) ? 'not-allowed' : 'pointer',
                                boxShadow: (uploading || !docType || !file) ? 'none' : '0 3px 10px rgba(43,70,99,0.2)'
                            }}
                        >
                            {uploading
                                ? <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Uploading...</>
                                : <><FaUpload /> Upload Document</>
                            }
                        </button>
                    </div>
                </div>
            </div>

            {/* Documents list */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem' }} />
                    Loading documents...
                </div>
            ) : docs.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '2.5rem',
                    background: 'white', border: '1px dashed #e2e8f0', borderRadius: '14px',
                    color: '#94a3b8'
                }}>
                    <FaFileAlt style={{ fontSize: '2rem', marginBottom: '0.75rem', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontWeight: 500 }}>No documents uploaded yet</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {docs.map(doc => {
                        const colors = docTypeColor[doc.doc_type] || { bg: '#f8fafc', color: '#374151', border: '#e2e8f0' };
                        const isDeleting = deletingId === doc.doc_id;
                        return (
                            <div key={doc.doc_id} style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '1rem 1.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                transition: 'box-shadow 0.2s'
                            }}>
                                {/* Icon */}
                                <div style={{
                                    width: '40px', height: '40px', borderRadius: '10px',
                                    background: colors.bg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.25rem', flexShrink: 0,
                                    border: `1px solid ${colors.border}`
                                }}>
                                    <FileIcon contentType={doc.content_type} />
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        <span style={{
                                            fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
                                            padding: '0.2rem 0.6rem', borderRadius: '20px',
                                            background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`
                                        }}>
                                            {doc.doc_type}
                                        </span>
                                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {doc.filename}
                                        </span>
                                    </div>
                                    {doc.upload_date && (
                                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                                            Uploaded {new Date(doc.upload_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                    <button
                                        title="Download"
                                        onClick={() => handleDownload(doc.doc_id, doc.filename)}
                                        style={{
                                                ...actionButtonStyle,
                                                minWidth: '118px',
                                            border: '1.5px solid #3b82f6',
                                            background: 'white',
                                            color: '#3b82f6',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s'
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                                    >
                                        <FaDownload /> Download
                                    </button>
                                    <button
                                        title="Delete"
                                        onClick={() => handleDelete(doc.doc_id)}
                                        disabled={isDeleting}
                                        style={{
                                            ...actionButtonStyle,
                                            minWidth: '118px',
                                            border: '1.5px solid #ef4444',
                                            background: 'white',
                                            color: '#ef4444',
                                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                                            opacity: isDeleting ? 0.6 : 1,
                                            transition: 'all 0.15s'
                                        }}
                                        onMouseEnter={e => { if (!isDeleting) e.currentTarget.style.background = '#fef2f2'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                                    >
                                        {isDeleting ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaTrash />}
                                        Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        </div>
    );
}
